import Phaser from 'phaser';
import { Player } from './Player';

export class Boss extends Phaser.Physics.Arcade.Sprite {
  public health: number;
  public maxHealth: number;
  private target: Player;
  private speed: number = 50;
  private healthBar!: Phaser.GameObjects.Graphics;
  private lastX: number = 0;
  private lastY: number = 0;
  private attackTimer!: Phaser.Time.TimerEvent;
  private isCharging: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number, target: Player) {
    super(scene, x, y, '');

    this.health = 200;
    this.maxHealth = 200;
    this.target = target;

    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Create boss sprite
    this.createBossTexture(scene);
    this.setTexture('enemy-boss');

    // Create health bar
    this.createHealthBar();

    // Setup physics
    this.setCollideWorldBounds(false);

    // Start attack pattern
    this.attackTimer = this.scene.time.addEvent({
      delay: 4000,
      callback: this.performNextAttack,
      callbackScope: this,
      loop: true
    });
  }

  private performNextAttack(): void {
    if (!this.active) return;

    const attackType = Phaser.Math.Between(0, 1);
    if (attackType === 0) {
      this.performShockwaveAttack();
    } else {
      this.performChargeAttack();
    }
  }

  private performShockwaveAttack(): void {
    if (!this.active) return;

    const projectileCount = 12;
    for (let i = 0; i < projectileCount; i++) {
      const angle = (i / projectileCount) * Math.PI * 2;
      const projectile = this.scene.add.circle(this.x, this.y, 5, 0xFF0000);
      this.scene.physics.add.existing(projectile);
      const body = projectile.body as Phaser.Physics.Arcade.Body;
      body.setVelocity(
        Math.cos(angle) * 150,
        Math.sin(angle) * 150
      );

      (projectile as any).enemyProjectile = true;
      (projectile as any).damage = 15;

      this.scene.time.delayedCall(3000, () => {
        projectile.destroy();
      });
    }
  }

  private performChargeAttack(): void {
    if (!this.active || !this.target) return;

    this.isCharging = true;
    this.setTint(0xff8c00); // Orange tint for charging

    const direction = new Phaser.Math.Vector2(
      this.target.x - this.x,
      this.target.y - this.y
    );
    direction.normalize();
    direction.scale(this.speed * 4); // Charge at 4x speed

    this.setVelocity(direction.x, direction.y);

    this.scene.time.delayedCall(800, () => {
      this.isCharging = false;
      this.clearTint();
      this.setVelocity(0, 0); // Stop briefly after charge
    });
  }

  private createBossTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 48; // Much larger
    const halfSize = size / 2;

    // Shadow (very large)
    graphics.fillStyle(0x000000, 0.25);
    graphics.fillEllipse(halfSize, halfSize + 18, 28, 8);

    // Body (massive, dark)
    graphics.fillStyle(0x1a1a1a, 1);
    graphics.fillEllipse(halfSize, halfSize + 3, 20, 24);
    graphics.fillStyle(0x2a2a2a, 1);
    graphics.fillEllipse(halfSize - 2, halfSize + 2, 16, 20);

    // Head (large and menacing)
    graphics.fillStyle(0x1a1a1a, 1);
    graphics.fillCircle(halfSize, halfSize - 6, 12);
    graphics.fillStyle(0x2a2a2a, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 7, 9);

    // Horns/spikes
    graphics.fillStyle(0x8B0000, 1);
    graphics.fillTriangle(
      halfSize - 9, halfSize - 14,
      halfSize - 7, halfSize - 20,
      halfSize - 5, halfSize - 14
    );
    graphics.fillTriangle(
      halfSize + 5, halfSize - 14,
      halfSize + 7, halfSize - 20,
      halfSize + 9, halfSize - 14
    );

    // Large ears
    graphics.fillStyle(0x0f0f0f, 1);
    graphics.fillCircle(halfSize - 9, halfSize - 12, 5);
    graphics.fillCircle(halfSize + 9, halfSize - 12, 5);
    graphics.fillStyle(0xFF0000, 0.3);
    graphics.fillCircle(halfSize - 9, halfSize - 11, 3);
    graphics.fillCircle(halfSize + 9, halfSize - 11, 3);

    // Eyes (intense red glow)
    graphics.fillStyle(0x8B0000, 1);
    graphics.fillCircle(halfSize - 4, halfSize - 7, 2.5);
    graphics.fillCircle(halfSize + 4, halfSize - 7, 2.5);
    graphics.fillStyle(0xFF0000, 1);
    graphics.fillCircle(halfSize - 4, halfSize - 7, 1.8);
    graphics.fillCircle(halfSize + 4, halfSize - 7, 1.8);
    graphics.fillStyle(0xFF4444, 0.7);
    graphics.fillCircle(halfSize - 4, halfSize - 7, 4);
    graphics.fillCircle(halfSize + 4, halfSize - 7, 4);

    // Fangs
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillTriangle(
      halfSize - 3, halfSize - 1,
      halfSize - 2, halfSize + 2,
      halfSize - 1, halfSize - 1
    );
    graphics.fillTriangle(
      halfSize + 1, halfSize - 1,
      halfSize + 2, halfSize + 2,
      halfSize + 3, halfSize - 1
    );

    // Armor/spikes on body
    graphics.fillStyle(0x404040, 1);
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const px = halfSize + Math.cos(angle) * 16;
      const py = halfSize + 3 + Math.sin(angle) * 20;
      graphics.fillTriangle(
        px - 2, py,
        px, py - 4,
        px + 2, py
      );
    }

    // Crown
    graphics.fillStyle(0xFFD700, 1);
    graphics.fillRect(halfSize - 8, halfSize - 16, 16, 3);
    for (let i = 0; i < 4; i++) {
      graphics.fillTriangle(
        halfSize - 6 + i * 4, halfSize - 16,
        halfSize - 5 + i * 4, halfSize - 20,
        halfSize - 4 + i * 4, halfSize - 16
      );
    }

    graphics.generateTexture('enemy-boss', size, size);
    graphics.destroy();
  }

  private createHealthBar(): void {
    this.healthBar = this.scene.add.graphics();
    this.healthBar.setDepth(100);
    this.updateHealthBar();
  }

  private updateHealthBar(): void {
    if (!this.healthBar) return;

    const barWidth = 50;
    const barHeight = 4;
    const x = this.x - barWidth / 2;
    const y = this.y - 30;

    this.healthBar.clear();

    // Background
    this.healthBar.fillStyle(0x000000, 0.5);
    this.healthBar.fillRect(x - 1, y - 1, barWidth + 2, barHeight + 2);

    // Red background
    this.healthBar.fillStyle(0x8B0000, 1);
    this.healthBar.fillRect(x, y, barWidth, barHeight);

    // Green health
    const healthPercent = this.health / this.maxHealth;
    this.healthBar.fillStyle(0x00FF00, 1);
    this.healthBar.fillRect(x, y, barWidth * healthPercent, barHeight);
  }

  update(): void {
    if (!this.target || !this.active) return;

    // Move towards player
    const direction = new Phaser.Math.Vector2(
      this.target.x - this.x,
      this.target.y - this.y
    );

    direction.normalize();
    direction.scale(this.speed);

    this.setVelocity(direction.x, direction.y);

    // Only update health bar position if moved significantly (performance optimization)
    const distMoved = Phaser.Math.Distance.Between(this.x, this.y, this.lastX, this.lastY);
    if (distMoved > 10) {
      this.updateHealthBar();
      this.lastX = this.x;
      this.lastY = this.y;
    }

    // Remove if too far from world
    const worldBounds = this.scene.cameras.main.worldView;
    const margin = 200;
    if (
      this.x < worldBounds.x - margin ||
      this.x > worldBounds.right + margin ||
      this.y < worldBounds.y - margin ||
      this.y > worldBounds.bottom + margin
    ) {
      this.destroy();
    }
  }

  takeDamage(amount: number): void {
    this.health -= amount;
    this.updateHealthBar();

    // Visual feedback
    this.setTint(0xff0000);
    this.scene.time.delayedCall(50, () => {
      this.clearTint();
    });

    if (this.health <= 0) {
      this.die();
    }
  }

  private die(): void {
    // Create death particle effect
    this.createDeathEffect();

    // Emit death event - bosses give more XP (5 gems)
    for (let i = 0; i < 5; i++) {
      const offsetX = Phaser.Math.Between(-20, 20);
      const offsetY = Phaser.Math.Between(-20, 20);
      this.scene.events.emit('enemy-killed', this.x + offsetX, this.y + offsetY);
    }

    // Destroy health bar
    if (this.healthBar) {
      this.healthBar.destroy();
    }

    this.destroy();
  }

  private createDeathEffect(): void {
    // Create large explosion effect
    const particles = this.scene.add.particles(this.x, this.y, 'enemy-boss', {
      speed: { min: 100, max: 250 },
      scale: { start: 1, end: 0 },
      lifespan: 500,
      quantity: 20,
      angle: { min: 0, max: 360 },
      tint: 0xFF0000
    });

    // Flash effect
    const flash = this.scene.add.graphics();
    flash.fillStyle(0xFF0000, 0.5);
    flash.fillCircle(this.x, this.y, 60);
    flash.setDepth(50);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 2,
      duration: 400,
      onComplete: () => {
        flash.destroy();
      }
    });

    // Auto-destroy particle emitter
    this.scene.time.delayedCall(600, () => {
      particles.destroy();
    });
  }

  destroy(fromScene?: boolean): void {
    if (this.healthBar) {
      this.healthBar.destroy();
    }
    super.destroy(fromScene);
  }
}
